// @ts-nocheck
import React, { useState, useEffect, useContext, useRef } from 'react';
import Router from 'next/router';
import { useQuery } from '@tanstack/react-query';
import SCInput from '@/components/sc-controls/form-controls/sc-input';
import SCCheckbox from '@/components/sc-controls/form-controls/sc-checkbox';
import Button from '@/components/button';
import Breadcrumbs from '@/components/breadcrumbs';
import { colors, fontSizes, layout, fontFamily, shadows } from '@/theme';
import Fetch from '@/utils/Fetch';
import ToastContext from '@/utils/toast-context';
import * as Enums from '@/utils/enums';
import SettingsMenu from '@/components/settings/menu';
import Tabs from '@/components/tabs';
import Helper from '@/utils/helper';
import ConfirmAction from '@/components/modals/confirm-action';
import OptionService from '@/services/option/option-service';
import LookupList from '@/components/lookup/list';
import Storage from '@/utils/storage';
import {Title, Space} from "@mantine/core";
import FieldSettings from "@/PageComponents/Settings/Field Settings/FieldSettings";
import { createFileRoute } from '@tanstack/react-router'



function CustomerSettings() {

  const toast = useContext(ToastContext);
  let currPath = "";
  if (Router.router) {
    currPath = Router.router.asPath;
  }

  const [selectedTab, setSelectedTab] = useState('Settings');
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState<any>(null);

  const [formIsDirty, setFormIsDirty] = useState(false);
  const [confirmOptions, setConfirmOptions] = useState(Helper.initialiseConfirmOptions());

  const [accessStatus, setAccessStatus] = useState(Enums.AccessStatus.None);
  const getAccessStatus = () => {
    let subscriptionInfo = Storage.getCookie(Enums.Cookie.subscriptionInfo);
    if (subscriptionInfo) {
      setAccessStatus(subscriptionInfo.AccessStatus);
    }
  }

  useEffect(() => {
    getAccessStatus();
  }, []);

  useEffect(() => {
    if (accessStatus === Enums.AccessStatus.LockedWithOutAccess) {
      Helper.nextRouter(Router.replace, "/");
    }
  }, [accessStatus]);

  Helper.preventRouteChange(formIsDirty, setFormIsDirty, setConfirmOptions, saveSettings);

  const { data: settingsData } = useQuery(
    ['customerSettings'],
    () => Fetch.get({ url: `/Option?module=${Enums.Module.Customer}` })
  );

  useEffect(() => {
    if (settingsData?.Results) {
      const settingInputs = OptionService.getSettingInputs(settingsData.Results);
      setInputs(settingInputs);
      setSettings(settingsData.Results);
    }
  }, [settingsData]);

  const [inputs, setInputs] = useState<any>({});
  const [inputErrors, setInputErrors] = useState<any>({});

  const handleInputChange = (e: any) => {
    setInputs({
      ...inputs,
      [e.name]: e.value
    });
    setFormIsDirty(true);
  }

  let settingGroups = OptionService.getSettingGroups(settings || []);
  const groupNames = Object.keys(settingGroups);

    const validate = (newSettings: any) => {
        let isValid = true;
        let settingsToCheck = [...newSettings].filter((x: any) => x.SystemType == 'System.String' && x.OptionName != 'Customer');

        let errors = {};
        settingsToCheck.forEach((setting: any) => {
            if (Helper.isNullOrWhitespace(setting.OptionValue)) {
            errors[setting.OptionName] = 'Cannot be empty';
            isValid = false;
            }
        });
        setInputErrors(errors);

        return isValid;
    };

  const checkForDuplicates = async (newSettings: any) => {

    // 1. check newSettings  

    let settingsToCheck = [...newSettings].filter((x: any) => x.SystemType == 'System.String' && x.OptionName != 'Customer');
    let settingValues = settingsToCheck.map((x: any) => x.OptionValue.trim().toLowerCase());
    let temp = [...new Set(settingValues)];

    if (settingValues.length != temp.length) {

      let duplicates = settingValues;
      temp.forEach((item: any) => {
        const i = duplicates.indexOf(item);
        duplicates = duplicates
          .slice(0, i)
          .concat(duplicates.slice(i + 1, duplicates.length));
      });

      if (duplicates.length > 0) {
        let errors = {};
        settingsToCheck.forEach((setting: any) => {
          if (duplicates.includes(setting.OptionValue.trim().toLowerCase())) {
            errors[setting.OptionName] = 'Duplicate';
          }
        });
        setInputErrors(errors);
      }

      return true;
    }

    // 2. check against mappings

    const columns = await Fetch.get({
      url: '/Employee/ColumnMapping',
      params: {
        model: 'CustomerList'
      }
    });

    const results = columns.Results;
    let labelsToCheck = results.map((x: any) => x.Label.trim().toLowerCase());

    let duplicateDetected = labelsToCheck.some((x: any) => {
      return settingValues.some((y: any) => y == x);
    });

    if (duplicateDetected) {
      let errors = {};
      settingsToCheck.forEach((setting: any) => {
        if (labelsToCheck.includes(setting.OptionValue.trim().toLowerCase())) {
          errors[setting.OptionName] = 'Duplicate';
        }
      });
      setInputErrors(errors);

      return true;
    }

    setInputErrors({});
    return false;
  };

  async function saveSettings() {
    setSubmitting(true);
    let newSettings = [...settings];
    newSettings.map(function (setting: any) {
      setting.OptionValue = inputs[setting.OptionName]?.toString() ?? null;
    });

    let isValid = validate(newSettings);

    if (isValid) {
        let isDuplicate = await checkForDuplicates(newSettings);

        if (isDuplicate) {
          toast.setToast!!!({
            message: 'A column with this name already exists. Please use another setting name.',
            show: true,
            type: 'error'
          });
        } else {
          const settingsRes = await Fetch.put({
            url: '/Option',
            params: newSettings,
            toastCtx: toast,
            statusIfNull: true
          });
      
          if (settingsRes.ResponseStatus == 200) {
            const rowUpdate = await Fetch.get({
              url: `/Option?module=${Enums.Module.Customer}`
            });
            setSettings(rowUpdate.Results);
            toast.setToast!!!({
              message: 'Customer settings updated successfully',
              show: true,
              type: 'success'
            });
          }
        }
    } else {
        toast.setToast!!!({
            message: 'There are errors on the page.',
            show: true,
            type: 'error'
        });
    }

    setSubmitting(false);
    setFormIsDirty(false);
    return true;
  }

    const getSettingDetails = (groupName: any) => {
        let group = settingGroups[groupName];
        let results = group.map((setting: any, index: any) => {
            switch (setting.SystemType) {
            case 'System.Boolean':
                return () => <SCCheckbox key={index}
                name={setting.OptionName}
                onChange={() => handleInputChange({ name: setting.OptionName, value: !inputs[setting.OptionName] })}
                value={Helper.parseBool(inputs[setting.OptionName])}
                label={setting.OptionName}
                />
            case 'System.String':
                return () =>
                <SCInput key={index}
                    onChange={handleInputChange}
                    error={inputErrors[setting.OptionName]}
                    label={setting.OptionName}
                    name={setting.OptionName}
                    hint={setting.Description}
                    required={setting.IsRequired}
                    value={inputs[setting.OptionName]}
                />

            default:
                return null;
            }
        });

        let rows = [];
        let counter = 0;
        for (let index = 0; index < Math.ceil(results.length / 2); index++) {  
            rows.push(() => {return (            
                <div className="row">
                    <div className="column"> {results[counter++]()}</div>
                    {results.length > counter ? <div className="column"> {results[counter++]()} </div> : <></>}
                    <style jsx>{`
                        .row {
                            display: flex;
                        }
                        .column {
                            display: flex;
                            flex-direction: column;
                            width: ${layout.inputWidth};
                        }
                        .column + .column {
                            margin-left: 1.25rem;
                        }
                    `}</style>
                </div>)});
        }

        return rows;
    };

  return (
    <div className="settings-container">
      {/*<div className="row">
        <div className="title">
          <Breadcrumbs
            crumbs={[
              { text: 'Settings', link: '/settings', suppressLink: true },
              { text: 'Customer', link: '/settings/customer/manage' },
            ]}
          />
        </div>
      </div>*/}
      <div className="row">
        <SettingsMenu urlPath={currPath} />
        <div className="settings-card">
          <Tabs
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            tabs={[
              { text: 'Settings', suppressCount: true },
              { text: 'Types', suppressCount: true },
              { text: 'Statuses', suppressCount: true },
              { text: 'Industry Types', suppressCount: true },
              { text: 'Media Types', suppressCount: true },
              { text: 'Designations', suppressCount: true },
            ]} />
          {(() => {
            switch (selectedTab) {
              case 'Settings':
                return (
                  <>

                      <Title order={2} c={'scBlue.9'} mt={'sm'}>Customer Settings</Title>
                      <FieldSettings module={Enums.Module.Customer} />

                    {/*<div className="row">
                      <div className="column">
                      </div>
                      <div className="column-end">
                        <Button disabled={accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess}
                          text={submitting ? "Saving" : "Save"} extraClasses="auto save" onClick={saveSettings} />
                      </div>
                    </div>
                    {groupNames.map(function (groupName, index) {
                      return (
                        <div key={index}>
                          <div className="section-title">
                            {groupName}
                          </div>
                          {getSettingDetails(groupName).map(item => {
                            return item();
                          })}
                        </div>
                      )
                    })}*/}
                  </>
                );
              case 'Types':
                return (
                  <LookupList key={0} type="customerType" accessStatus={accessStatus} />
                );
              case 'Statuses':
                return (
                  <LookupList key={1} type="customerStatus" accessStatus={accessStatus} />
                );
              case 'Industry Types':
                return (
                  <LookupList key={2} type="industryType" accessStatus={accessStatus} />
                );
              case 'Media Types':
                return (
                  <LookupList key={3} type="mediaType" accessStatus={accessStatus} />
                );
              case 'Designations':
                return (
                  <LookupList key={4} type="designation" accessStatus={accessStatus} />
                );
              default:
                return null;
            }
          })()}
        </div>
      </div>

      <ConfirmAction options={confirmOptions} setOptions={setConfirmOptions} />
      <Space h={80} />

      <style jsx>{`
        .row {
          display: flex;
          justify-content: space-between;
        }
        .column {
          display: flex;
          flex-direction: column;
          width: ${layout.inputWidth};
        }
        .column-end {
          align-items: flex-end;
        }
        .column-margin {
          margin-left: 24px;
        }
       
        .title {
          color: ${colors.darkPrimary};
          position: relative;
          width: 100%;
        }
        .title h1 {
          font-size: 24px;
          margin: 6px 0 2rem 0;
        }
        .title p {
          font-size: 14px;
          margin: 0;
        }                
        h2 {
          color: ${colors.darkPrimary};
          font-size: 1.75rem;
          font-weight: lighter;
          margin: 1rem 0 -1rem;
        }
        .section-title {
          background-color: ${colors.background};
          border-radius: ${layout.cardRadius};
          box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
          color: ${colors.bluePrimary};
          font-size: 1.25rem;
          font-weight: bold;
          margin-top: 2rem;
          opacity: 0.9;
          padding: 1rem;
        }
        .small-title {
          color: ${colors.white};
          font-size: 0.875rem;
          font-weight: bold;
          margin-top: 2rem;
        }
        .container :global(.input-container) {
          width: 50%;
        }
        .title :global(.save) {
          margin: 0;
          position: absolute;
          right: 0;
          top: 0;
        }
      `}</style>
    </div>
  );
}

CustomerSettings.displayName = 'CustomerSettings';

export default CustomerSettings;

export const Route = createFileRoute('/_authenticated/settings/customer/manage')({ component: CustomerSettings })
